import React, { Component, createRef } from 'react';
import Modal from 'rc-dialog';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';

import ModalStore, { ModalProps } from 'stores/ModalStore';
import { cropImageFile } from 'helpers/canvas';
import MenuDesignStore, {
  MenuDesignSteps,
} from 'stores/Modals/MenuDesignStore';

import Button from 'components/Button/Button';
import ModalBody from 'components/Modals/Components/ModalBody/ModalBody';
import ModalHeader from 'components/Modals/Components/ModalHeader/ModalHeader';
import ModalControls from 'components/Modals/Components/ModalControls/ModalControls';
import {
  ImageEditor,
  ImageEditorRef,
} from 'components/ImageEditor/ImageEditor';
import FileUploader from 'components/Form/Fields/FileUpload/FileUploader';
import Spinner from 'components/Spinner/Spinner';
import style from './MenuDesignModal.module.scss';
import { Label } from 'components/Form/Label/Label';
import MenuDesignPreview from './MenuDesignPreview/MenuDesignPreview';

@observer
class MenuDesignModal extends Component<ModalProps> {
  private _store = new MenuDesignStore();
  private _editorRef = createRef<ImageEditorRef>();

  render() {
    return (
      <Modal
        {...this.props}
        className={style.modal}
        destroyOnClose
        animation="zoom"
        maskAnimation="fade"
        visible
      >
        <ModalHeader>
          <FormattedMessage id="title.menuDesign" />
        </ModalHeader>

        <ModalBody>
          <div className={style.content}>
            {!this._store.isInit && <Spinner contain />}
            {this._renderContentStep()}
          </div>
        </ModalBody>

        <ModalControls className={style.controls}>
          {this._renderControls()}
        </ModalControls>
      </Modal>
    );
  }

  private _renderContentStep = () => {
    const { step, form, backgroundImageUrl } = this._store;

    switch (step) {
      case MenuDesignSteps.Default:
        return (
          <div className={style.form__group}>
            {!!backgroundImageUrl && (
              <div className={style.bg}>
                <Label textId="modal.menu.design.current" />
                <img src={backgroundImageUrl} alt="bg" />
                <div className={style.bg__subtitle}>
                  <FormattedMessage id="modal.menu.design.bg.subtitle" />
                </div>
              </div>
            )}

            <FileUploader file={''} onFileChange={this._handleNewImageChange} />
          </div>
        );
      case MenuDesignSteps.Editor:
        return (
          <>
            <div className={style.editor}>
              <ImageEditor
                editorRef={this._editorRef}
                image={form.file!}
                width={560}
                height={315}
                border={20}
                onChange={this._handleEditorImageChange}
              />
            </div>
            <div className={style.preview}>
              <MenuDesignPreview
                file={this._store.form.editedFile}
                width={60}
                height={120}
                title="modal.menu.design.preview.phone"
              />
              <MenuDesignPreview
                file={this._store.form.editedFile}
                width={150}
                height={120}
                title="modal.menu.design.preview.tablet"
              />
              <MenuDesignPreview
                file={this._store.form.editedFile}
                width={215}
                height={120}
                title="modal.menu.design.preview.desktop"
              />
            </div>
          </>
        );
    }
  };

  private _renderControls = () => {
    const { step, loading, setStep } = this._store;

    switch (step) {
      case MenuDesignSteps.Default:
        return (
          <>
            <Button
              className={style.btn}
              styleType="ghost"
              onClick={this._handleClose}
            >
              <FormattedMessage id="button.cancel" />
            </Button>
          </>
        );
      case MenuDesignSteps.Editor:
        return (
          <>
            <Button
              className={style.btn}
              styleType="ghost"
              onClick={() => setStep(MenuDesignSteps.Default)}
            >
              <FormattedMessage id="button.back" />
            </Button>

            <Button
              className={style.btn}
              styleType="primary"
              disabled={loading}
              onClick={this._handleSubmit}
            >
              <FormattedMessage id="button.saveChanges" />
            </Button>
          </>
        );
    }
  };

  private _handleNewImageChange = (file: File) => {
    const { form, setStep } = this._store;

    setStep(MenuDesignSteps.Editor);

    form.setValue('file', file);
    form.setValue('editedFile', file);
  };

  private _handleClose = () => {
    ModalStore.closeModal();
  };

  private _handleSubmit = async () => {
    await this._store.submit();
    ModalStore.closeModal();
  };

  private _handleEditorImageChange = () => {
    const { file, setValue } = this._store.form;
    if (!file) return;

    const coords = this._editorRef.current!.getCroppingRect();

    cropImageFile(file, coords, editedFile => {
      setValue('editedFile', editedFile);
    });
  };
}

export default MenuDesignModal;
